import { useEffect, useState } from "react";
import { api, fmtBRL, fmtInt } from "@/lib/api";
import { Plus, MagnifyingGlass, ArrowsClockwise, Tag, Trash, PencilSimple, X, CheckCircle, Package } from "@phosphor-icons/react";
import HintButton from "@/components/HintButton";

const EMPTY = { sku: "", name: "", description: "", category: "Geral", unit: "UN", price: 0, cost: 0, stock: 0, active: true, image_url: "" };

export default function Products() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | product object
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [skuPreview, setSkuPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { search: search || undefined, only_active: onlyActive } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  // `search` é aplicada manualmente (Enter/botão Buscar) — não re-dispara o load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [onlyActive]);

  const sync = async () => {
    setSyncing(true);
    try { await api.post("/products/sync"); setTimeout(load, 2000); }
    catch (e) { alert(e?.response?.data?.detail || "Conecte o Bling primeiro"); }
    finally { setSyncing(false); }
  };

  const openNew = () => { setForm(EMPTY); setEditing("new"); setError(""); setSkuPreview(null); };
  const openEdit = (p) => {
    setForm({ sku: p.sku, name: p.name, description: p.description || "", category: p.category || "Geral", unit: p.unit || "UN", price: p.price || 0, cost: p.cost || 0, stock: p.stock || 0, active: p.active, image_url: p.image_url || "" });
    setEditing(p);
    setError("");
    setSkuPreview(p.sku_parsed);
  };

  const onSkuChange = async (v) => {
    setForm({ ...form, sku: v });
    if (v && v.length >= 4) {
      try {
        const { data } = await api.get("/sku/parse", { params: { sku: v } });
        setSkuPreview(data);
      } catch (e) { console.warn("Product search falhou:", e?.response?.status); }
    } else {
      setSkuPreview(null);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, cost: parseFloat(form.cost) || 0, stock: parseInt(form.stock) || 0 };
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing.id}`, payload);
      setEditing(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const del = async (id) => {
    if (!confirm("Remover produto?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500 mb-2">CATÁLOGO</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Cadastro de Produtos</h1>
          <HintButton text="SKUs como POP050 (Pop Cup · 50 un) ou TWS100 (Twister · 100 un) definem automaticamente a quantidade de copos por pedido." />
        </div>
        <div className="flex items-center gap-2">
          <button className="btn btn-ghost" onClick={openNew} data-testid="add-product"><Plus size={16} weight="bold" /> Novo Produto</button>
          <button className="btn btn-klein" onClick={sync} disabled={syncing} data-testid="sync-products"><ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} /> {syncing ? "Sincronizando..." : "Importar do Bling"}</button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[280px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input className="input pl-10" placeholder="Buscar por SKU ou nome" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="prod-search" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} data-testid="prod-only-active" /> Somente ativos
        </label>
        <button className="btn btn-ghost" onClick={load}>Buscar</button>
      </div>

      <div className="border border-black bg-white overflow-x-auto">
        <table className="table" data-testid="products-table">
          <thead><tr>
            <th>SKU</th><th>Nome</th><th>Categoria</th><th className="text-center">Tipo</th><th className="text-center">Un/SKU</th><th className="text-right">Preço</th><th className="text-right">Estoque</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-10 text-zinc-400">Carregando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10">
                <Package size={40} weight="thin" className="mx-auto text-zinc-300 mb-2" />
                <div className="overline text-zinc-400 mb-1">SEM PRODUTOS</div>
                <div className="text-zinc-500 text-sm">Conecte o Bling para importar produtos ou cadastre manualmente.</div>
              </td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.sku}`}>
                <td className="font-mono font-semibold">{p.sku}</td>
                <td className="max-w-md truncate">{p.name}</td>
                <td className="text-zinc-600 text-xs">{p.category}</td>
                <td className="text-center text-xs">{p.sku_parsed?.type || "—"}</td>
                <td className="text-center font-mono font-bold">{p.sku_parsed?.units_per_unit || 1}</td>
                <td className="text-right font-mono">{fmtBRL(p.price)}</td>
                <td className="text-right font-mono">{fmtInt(p.stock)}</td>
                <td>{p.active ? <span className="text-xs text-[#00A300] font-bold">● ATIVO</span> : <span className="text-xs text-zinc-400">● INATIVO</span>}</td>
                <td className="text-right whitespace-nowrap">
                  <button className="text-xs underline mr-3" onClick={() => openEdit(p)} data-testid={`prod-edit-${p.sku}`}><PencilSimple size={12} className="inline mr-1" />Editar</button>
                  <button className="text-xs underline text-red-600" onClick={() => del(p.id)} data-testid={`prod-del-${p.sku}`}><Trash size={12} className="inline mr-1" />Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="glass max-w-2xl w-full p-8 my-8" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-zinc-500">{editing === "new" ? "NOVO PRODUTO" : "EDITAR PRODUTO"}</div>
                <h2 className="font-head text-2xl font-black">{editing === "new" ? "Cadastrar" : form.name}</h2>
              </div>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label">SKU *</label>
                  <input className="input font-mono" required value={form.sku} onChange={(e) => onSkuChange(e.target.value)} placeholder="POP050" data-testid="prod-sku" />
                  {skuPreview && skuPreview.prefix && (
                    <div className="text-xs text-[#00A300] mt-1 font-mono">→ {skuPreview.type} · {skuPreview.units_per_unit} un/SKU</div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="label">Nome *</label>
                  <input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" />
                </div>
              </div>
              <div>
                <label className="label">Descrição</label>
                <textarea className="input min-h-[60px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="prod-desc" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="label">Categoria</label>
                  <input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="prod-cat" />
                </div>
                <div>
                  <label className="label">Unidade</label>
                  <input className="input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="prod-unit" />
                </div>
                <div>
                  <label className="label">Preço (R$)</label>
                  <input type="number" step="0.01" className="input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="prod-price" />
                </div>
                <div>
                  <label className="label">Custo (R$)</label>
                  <input type="number" step="0.01" className="input" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="prod-cost" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="label">Estoque</label>
                  <input type="number" className="input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="prod-stock" />
                </div>
                <div>
                  <label className="label">URL da imagem</label>
                  <input className="input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="opcional" data-testid="prod-img" />
                </div>
                <label className="flex items-center gap-2 text-sm pb-3">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="prod-active" />
                  Produto ativo
                </label>
              </div>
              {error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] text-sm px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" className="btn btn-ghost flex-1" onClick={() => setEditing(null)}>Cancelar</button>
                <button type="submit" className="btn flex-1" data-testid="prod-save"><CheckCircle size={16} weight="bold" /> Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
